// Considered: no existing helper — the provenance-chip decision (SC2: "a provenance chip iff
// provenanceTier ≠ attested or validationStatus = unvalidated") was previously scattered inline
// wherever an entry rendered. Pure function so `StreamEntryMolecule` stays declarative and the
// branching is unit-testable without mounting a component.
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx `MSChip` usage (CH_EXPLAIN's `calculated`
// chip, CH_VALIDATE's `claimed`/`needs validation` chips).

import type { ProvenanceTier } from 'types/aspect';
import type { StreamEntry } from 'types/stream';
import type { ChipAtomProps } from '../../components/atoms/Chip/ChipAtom';

/**
 * The chip label for a tier that is neither attested nor platform-computed. Only `imported` and
 * `migrated` name their own origin; every other such tier is a plain human claim.
 */
const CLAIM_LABEL_KEYS: Partial<Record<ProvenanceTier, string>> = {
  imported: 'stream.chips.imported',
  migrated: 'stream.chips.migrated',
};

export interface StreamProvenanceChipConfig {
  icon: string;
  labelKey: string;
  tone: NonNullable<ChipAtomProps['tone']>;
  dashed?: boolean;
}

/**
 * The provenance chip(s) an entry shows, per SC2: no chip when `provenanceTier === 'attested'`
 * AND `validationStatus === 'validated'`. Otherwise:
 * - `calculated`/`inferred` → the ⚙ "calculated" chip (`tone="calc"`).
 * - `imported`/`migrated` → the provenance chip for that tier (`tone="claim"`).
 * - any other non-attested tier (`claimed`) → the "claimed" chip.
 * - `validationStatus === 'unvalidated'` → the dashed "needs validation" chip, IN ADDITION to
 *   the provenance chip above (an unvalidated `claimed` entry shows both — README §1c) —
 *   UNLESS the aspect is platform-authored (`aspectUserAuthorable === false`, i.e. its
 *   `valuePolicy.automation` is `'auto'`). Validation asks whether a responsible human stands
 *   behind a value somebody asserted; a value the platform computes has no author to attest and
 *   no act that could ever clear the chip. `center_edge_gravity` — a pure function of
 *   (velocity, activeEdgeCount, urgency) — showed "needs validation" indefinitely because of
 *   this. `writeEntry` now records such entries as `validated` on the way in, but entries
 *   already in the ledger keep the status they were written with (it is part of the hash
 *   preimage and cannot be back-corrected), so the rule is enforced here too.
 * - `validationStatus === 'rejected'` → the "rejected" chip (`tone="warn"`), so a Reject act
 *   leaves a visible mark on re-render instead of the entry looking validated.
 * - `backdated` → the "backdated" chip (`stream.d.ts:35` contract: `recordedAt - observedAt > 24h`).
 */
export function resolveStreamProvenanceChips(
  entry: Pick<StreamEntry, 'provenanceTier' | 'validationStatus'> & {
    backdated?: boolean;
    /** Absent ⇒ authorable, matching the server's own default for an unresolved aspect. */
    aspectUserAuthorable?: boolean;
  },
): StreamProvenanceChipConfig[] {
  const chips: StreamProvenanceChipConfig[] = [];

  if (entry.provenanceTier === 'calculated' || entry.provenanceTier === 'inferred') {
    chips.push({ icon: 'lucide:settings', labelKey: 'stream.chips.calculated', tone: 'calc' });
  } else if (entry.provenanceTier !== 'attested' && entry.provenanceTier !== 'integration_verified') {
    const labelKey = CLAIM_LABEL_KEYS[entry.provenanceTier] ?? 'stream.chips.claimed';
    chips.push({ icon: 'lucide:log-in', labelKey, tone: 'claim' });
  }

  const validationApplies = entry.aspectUserAuthorable !== false;

  if (validationApplies && entry.validationStatus === 'unvalidated') {
    chips.push({ icon: 'lucide:circle', labelKey: 'stream.chips.needsValidation', tone: 'warn', dashed: true });
  } else if (validationApplies && entry.validationStatus === 'rejected') {
    chips.push({ icon: 'lucide:x-circle', labelKey: 'stream.chips.rejected', tone: 'warn' });
  }

  if (entry.backdated) {
    chips.push({ icon: 'lucide:history', labelKey: 'stream.chips.backdated', tone: 'neutral' });
  }

  return chips;
}

export default resolveStreamProvenanceChips;
